/**
 * O diagrama que substituiu o cubo no topo da home.
 *
 * Ele não é enfeite: mostra, em três passos, como cada vídeo é feito —
 * notícia, duas fontes conferidas, explicação de 50 segundos. É a prova
 * desenhada do que o texto ao lado promete, e é a única peça animada da
 * página. Desenho técnico: tinta, uma cor de acento e rótulos em mono.
 *
 * Movimento com função: o traço se desenha na ordem do processo e os
 * vistos entram quando a fonte é conferida. Quem pede menos movimento
 * (prefers-reduced-motion) recebe o estado final, não uma tela vazia.
 */

const CICLO = 9; // segundos, igual ao .jg-anim

// Cada passo entra no seu tempo. Em segundos, para ler igual à narrativa.
const T = {
  traco1: 0.3,
  fonte1: 1.2,
  fonte2: 1.9,
  traco2: 2.6,
  video: 3.2,
} as const;

function atraso(s: number) {
  return { animationDelay: `${s}s` };
}

export function ProcessoDiagram() {
  return (
    <figure className="m-0">
      <svg
        viewBox="0 0 280 320"
        className="w-full max-w-[320px] text-jg-fg sm:max-w-none"
        role="img"
        aria-label="Como cada vídeo é feito: uma notícia da semana, conferida em duas fontes, vira uma explicação de cinquenta segundos."
        fill="none"
        strokeLinecap="square"
      >
        <g aria-hidden="true">
          {/* ── 1. a notícia ──────────────────────────────────── */}
          <rect
            x="40"
            y="10"
            width="200"
            height="56"
            stroke="currentColor"
            strokeWidth="1.5"
          />
          <text
            x="52"
            y="30"
            className="fill-current font-mono text-[8px] uppercase tracking-[0.18em]"
          >
            notícia da semana
          </text>
          <line x1="52" y1="42" x2="196" y2="42" stroke="currentColor" strokeWidth="1" opacity="0.35" />
          <line x1="52" y1="52" x2="150" y2="52" stroke="currentColor" strokeWidth="1" opacity="0.35" />

          {/* traço até as fontes */}
          <path
            d="M140 66 V96"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeDasharray="30"
            style={{ ...atraso(T.traco1), ["--jg-len" as string]: "30" }}
            className="jg-anim [animation-name:jg-draw]"
            data-reveal
          />
          <path d="M136 90 L140 96 L144 90" stroke="currentColor" strokeWidth="1.5" />

          {/* ── 2. as duas fontes ─────────────────────────────── */}
          {[
            { x: 40, rotulo: "fonte 1", delay: T.fonte1 },
            { x: 146, rotulo: "fonte 2", delay: T.fonte2 },
          ].map((fonte) => (
            <g key={fonte.rotulo}>
              <rect
                x={fonte.x}
                y="96"
                width="94"
                height="56"
                stroke="currentColor"
                strokeWidth="1.5"
              />
              <text
                x={fonte.x + 12}
                y="118"
                className="fill-current font-mono text-[8px] uppercase tracking-[0.18em]"
              >
                {fonte.rotulo}
              </text>
              <line
                x1={fonte.x + 12}
                y1="130"
                x2={fonte.x + 62}
                y2="130"
                stroke="currentColor"
                strokeWidth="1"
                opacity="0.35"
              />
              {/* o visto: entra quando a fonte foi conferida */}
              <g
                className="jg-anim [animation-name:jg-stamp]"
                style={atraso(fonte.delay)}
                data-reveal
              >
                <circle
                  cx={fonte.x + 72}
                  cy="130"
                  r="11"
                  className="stroke-jg-accent"
                  strokeWidth="1.5"
                />
                <path
                  d={`M${fonte.x + 67} 130 l3.5 4 l7.5 -8`}
                  className="stroke-jg-accent"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </g>
            </g>
          ))}

          {/* traço até o vídeo */}
          <path
            d="M140 152 V182"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeDasharray="30"
            style={{ ...atraso(T.traco2), ["--jg-len" as string]: "30" }}
            className="jg-anim [animation-name:jg-draw]"
            data-reveal
          />
          <path d="M136 176 L140 182 L144 176" stroke="currentColor" strokeWidth="1.5" />

          {/* ── 3. a explicação ───────────────────────────────── */}
          <rect
            x="40"
            y="182"
            width="200"
            height="106"
            stroke="currentColor"
            strokeWidth="1.5"
          />

          <g
            className="jg-anim [animation-name:jg-appear]"
            style={atraso(T.video)}
            data-reveal
          >
            {/* play */}
            <path
              d="M128 222 l22 13 l-22 13 z"
              className="fill-jg-accent"
            />
            {/* a faixa de legenda, que todo vídeo tem */}
            <rect x="56" y="262" width="168" height="10" className="fill-jg-accent" opacity="0.18" />
            <rect x="56" y="262" width="96" height="10" className="fill-jg-accent" />
          </g>

          <text
            x="52"
            y="202"
            className="fill-current font-mono text-[8px] uppercase tracking-[0.18em]"
          >
            explicação
          </text>

          {/* cota de 50 s, do jeito de desenho técnico */}
          <g className="stroke-current" strokeWidth="1" opacity="0.55">
            <line x1="252" y1="182" x2="252" y2="288" />
            <line x1="248" y1="182" x2="256" y2="182" />
            <line x1="248" y1="288" x2="256" y2="288" />
          </g>
          <text
            x="266"
            y="239"
            transform="rotate(90 266 239)"
            textAnchor="middle"
            className="fill-current font-mono text-[8px] uppercase tracking-[0.18em]"
            opacity="0.55"
          >
            50 s
          </text>
        </g>
      </svg>

      {/* A legenda não repete o texto da seção: só numera o desenho, como
          num documento técnico. */}
      <figcaption className="jg-eyebrow mt-5">
        fig. 1 — o caminho de cada vídeo
      </figcaption>
    </figure>
  );
}

export const CICLO_SEGUNDOS = CICLO;
